import React from 'react';
import { useLocation } from 'react-router-dom';
import { FileText, History, Target, Award, Users, Building } from 'lucide-react';

const GenericAboutPage = () => {
  const location = useLocation();
  const path = location.pathname;

  const pageContent = {
    '/hakkimizda/tarihce': {
      title: 'Grubumuzun Tarihçesi',
      icon: History,
      content: [
        '1900 yılında kurulan TurkishBank, 120 yılı aşkın bir süre boyunca Türkiye ve bölgedeki finansal piyasalarda önemli bir rol oynamaktadır.',
        '1996 yılında ise Turkish Menkul Değerler A.Ş., grubun sermaye piyasalarındaki varlığını güçlendirmek amacıyla kurulmuştur.',
        'Bugün Türkiye, Kıbrıs ve İngiltere\'de faaliyet gösteren grup, modern bankacılık ve yatırım hizmetleri sunmaya devam etmektedir.'
      ]
    },
    '/hakkimizda/misyon-vizyon': {
      title: 'Misyon & Vizyon',
      icon: Target,
      content: [
        '**Misyonumuz:** Müşterilerimize en kaliteli yatırım hizmetlerini sunarak, finansal hedeflerine ulaşmalarında güvenilir bir ortak olmak.',
        '**Vizyonumuz:** Bölgenin en güvenilir ve tercih edilen yatırım kuruluşu olmak, yenilikçi çözümler sunarak sektörde öncü olmak.',
        'Şeffaflık, dürüstlük ve müşteri memnuniyeti temel değerlerimizdir.'
      ]
    },
    '/hakkimizda/kurumsal-degerlerimiz': {
      title: 'Kurumsal Değerlerimiz',
      icon: Award,
      content: [
        '**Dürüstlük:** Tüm iş süreçlerimizde en yüksek etik standartları benimsiyoruz.',
        '**Müşteri Odaklılık:** Müşterilerimizin ihtiyaçlarını önceliğimiz olarak görüyoruz.',
        '**Yenilikçilik:** Sürekli gelişim ve yenilikçi çözümlerle sektörde fark yaratıyoruz.',
        '**Profesyonellik:** Uzman kadromuz ve profesyonel yaklaşımımızla hizmet sunuyoruz.'
      ]
    },
    '/hakkimizda/kurumsal-yonetim': {
      title: 'Kurumsal Yönetim',
      icon: Users,
      content: [
        'Turkish Menkul Değerler, kurumsal yönetim ilkelerine tam uyum sağlayarak faaliyetlerini sürdürmektedir.',
        'Yönetim Kurulumuz, bağımsız üyeler de dahil olmak üzere, şirketin stratejik yönünü belirler ve denetler.',
        'Kurumsal sosyal sorumluluk ve sürdürülebilirlik anlayışımızla geleceğe değer katıyoruz.'
      ]
    },
    '/hakkimizda/rapor': {
      title: 'Raporlar',
      icon: FileText,
      content: [
        'Finansal raporlarımız, faaliyet raporlarımız ve diğer kurumsal belgelerimizi bu bölümde bulabilirsiniz.',
        'Tüm raporlarımız bağımsız denetimden geçmiş olup, şeffaflık ilkemiz gereği kamuoyu ile paylaşılmaktadır.'
      ],
      reports: [
        { name: '2024 Yılı Faaliyet Raporu', date: '2025-01-15' },
        { name: '2024 Q4 Finansal Sonuçlar', date: '2025-01-20' },
        { name: '2023 Yıllık Rapor', date: '2024-03-01' },
        { name: 'Sürdürülebilirlik Raporu 2024', date: '2024-06-15' }
      ]
    },
    '/hakkimizda/istiraklerimiz': {
      title: 'İştiraklerimiz ve Grup Şirketlerimiz',
      icon: Building,
      content: [
        'TurkishBank Group bünyesinde finansal hizmetlerin farklı alanlarında faaliyet gösteren birden fazla şirket bulunmaktadır.',
        'Türk Bankası, Türk Sigorta, TurkishBank UK, Turkish Menkul Değerler, Turkish Digital Teknolojiler ve Allied Turkish IBU grubumuzun önemli iştiraklerindendir.',
        'Her bir iştirak, kendi alanında uzmanlaşarak müşterilerimize kapsamlı finansal çözümler sunmaktadır.'
      ]
    },
    '/hakkimizda/haberler': {
      title: 'Bizden Haberler',
      icon: FileText,
      content: [
        'Şirketimiz hakkındaki en güncel haberler, basın bültenleri ve duyuruları bu sayfada bulabilirsiniz.'
      ]
    }
  };

  const currentPage = pageContent[path] || pageContent['/hakkimizda/bizi-taniyin'];
  const IconComponent = currentPage.icon;

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <div className="relative h-80 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 flex items-center">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.02) 35px, rgba(255,255,255,.02) 70px)'
          }}></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="flex items-center space-x-4 mb-4">
            <div className="bg-orange-500 p-3 rounded-lg">
              <IconComponent className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-5xl font-bold text-white">{currentPage.title}</h1>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="prose prose-lg max-w-none text-slate-700 space-y-6">
              {currentPage.content.map((paragraph, index) => (
                <p key={index} className="leading-relaxed">
                  {paragraph.split('**').map((part, i) => 
                    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
                  )}
                </p>
              ))}
            </div>

            {/* Reports List */}
            {currentPage.reports && (
              <div className="mt-8 space-y-3">
                {currentPage.reports.map((report, index) => (
                  <div key={index} className="bg-slate-50 rounded-lg p-4 flex items-center justify-between hover:bg-orange-50 transition-colors border border-slate-200">
                    <div className="flex items-center space-x-3">
                      <FileText className="w-5 h-5 text-orange-600" />
                      <div>
                        <h3 className="font-semibold text-slate-900">{report.name}</h3>
                        <p className="text-sm text-slate-500">{report.date}</p>
                      </div>
                    </div>
                    <button className="text-orange-600 hover:text-orange-700 font-semibold text-sm">
                      İndir
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GenericAboutPage;